/**
 * Standard Technical Log Book totalCarriedForward cycle counters for component TSR (unit Cycles).
 * Keep aligned with technicalLogBookCarriedForward.js BUILT_IN cycle keys + diskWear.
 */
import { DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION } from '../constants/technicalLogBookDefinition';
import { parseCyclesValue } from '../components/components/componentFormUtils';

export const TLB_CYCLE_CARRIED_FORWARD_KEYS = [
  'lhEngineCyclesCT',
  'lhEngineCyclesPT',
  'lhEngineCyclesIMP',
  'rhEngineCyclesCT',
  'rhEngineCyclesPT',
  'rhEngineCyclesIMP',
  'lgLeftOvspdCy',
  'lgRightOvspdCy',
  'nrAndHydraulicCycles',
  'lgNoseOvspdCy',
  'lgExtRecCyc',
  'rotorBrakeRbcy',
  'mgbTorqueTcy',
  'diskWear'
];

/** Hour / Hobbs counters for AMP/ADSB HOURS schedule tlbCarriedForwardKey (keep aligned with server util). */
export const TLB_HOURS_CARRIED_FORWARD_KEYS = [
  'airframeHours',
  'engine1Hours',
  'engine2Hours',
  'hobbs',
  'hobbsAC'
];

const DEFAULT_HOURS_LABELS_BY_KEY = {
  airframeHours: 'Airframe Hours',
  engine1Hours: 'Engine 1 Hours',
  engine2Hours: 'Engine 2 Hours',
  hobbs: 'HOBBS METER',
  hobbsAC: 'HOBBS METER AC'
};

const DEFAULT_LABELS_BY_KEY = (() => {
  const map = {};
  const section = (DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION?.sections || []).find((s) => s.id === 'totals');
  const fields = section?.fields || [];
  for (const f of fields) {
    const path = f.path || '';
    const m = path.match(/^totalCarriedForward\.([a-zA-Z0-9_]+)$/);
    if (m && f.label) {
      map[m[1]] = String(f.label).trim();
    }
  }
  for (const key of TLB_CYCLE_CARRIED_FORWARD_KEYS) {
    if (!map[key]) {
      map[key] = key;
    }
  }
  return map;
})();

/**
 * @param {object|null|undefined} definition - TLB format definition
 * @returns {Record<string, string>} key → display label (prefers carried-forward field labels)
 */
export function buildTlbCycleLabelsFromDefinition(definition) {
  const labels = { ...DEFAULT_LABELS_BY_KEY };
  const sections = definition?.sections;
  if (!Array.isArray(sections)) {
    return labels;
  }
  const totals = sections.find((s) => s && s.id === 'totals');
  if (!totals) return labels;

  const ingestField = (path, label) => {
    if (!path || !label) return;
    const m = String(path).match(/^totalCarriedForward\.([a-zA-Z0-9_]+)$/);
    if (m) {
      labels[m[1]] = String(label).trim();
    }
  };

  for (const f of totals.fields || []) {
    if (f.visible === false) continue;
    ingestField(f.path, f.label);
  }
  for (const cf of totals.customFields || []) {
    if (cf.visible === false) continue;
    const key = cf.key || cf.name;
    if (key && TLB_CYCLE_CARRIED_FORWARD_KEYS.includes(key)) {
      labels[key] = String(cf.label || cf.name || key).trim();
    }
  }

  return labels;
}

/**
 * @param {object|null|undefined} definition - TLB format definition
 * @returns {Record<string, string>} key → display label for hour/Hobbs counters
 */
export function buildTlbHoursLabelsFromDefinition(definition) {
  const labels = { ...DEFAULT_HOURS_LABELS_BY_KEY };
  const sections = definition?.sections;
  if (!Array.isArray(sections)) {
    return labels;
  }
  const totals = sections.find((s) => s && s.id === 'totals');
  if (!totals) return labels;

  const ingestField = (path, label) => {
    if (!path || !label) return;
    const m = String(path).match(/^totalCarriedForward\.([a-zA-Z0-9_]+)$/);
    if (m && TLB_HOURS_CARRIED_FORWARD_KEYS.includes(m[1])) {
      labels[m[1]] = String(label).trim();
    }
  };

  for (const f of totals.fields || []) {
    if (f.visible === false) continue;
    ingestField(f.path, f.label);
  }
  for (const cf of totals.customFields || []) {
    if (cf.visible === false) continue;
    const key = cf.key || cf.name;
    if (key && TLB_HOURS_CARRIED_FORWARD_KEYS.includes(key)) {
      labels[key] = String(cf.label || cf.name || key).trim();
    }
  }

  return labels;
}

export function formatTlbCycleLiveDisplay(raw) {
  if (raw == null || raw === '') return '—';
  const n = parseCyclesValue(String(raw));
  return Number.isFinite(n) ? String(n) : '—';
}

/**
 * @param {object|null|undefined} definition
 * @param {object|null|undefined} liveValues - currentUsageTotals.values
 * @returns {Array<{ key: string, label: string, liveRaw: number|null, liveDisplay: string }>}
 */
export function buildTlbCycleOptions(definition, liveValues) {
  const labels = buildTlbCycleLabelsFromDefinition(definition);
  const values = liveValues && typeof liveValues === 'object' ? liveValues : {};

  return TLB_CYCLE_CARRIED_FORWARD_KEYS.map((key) => {
    const raw = values[key];
    const liveRaw =
      raw != null && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : null;
    return {
      key,
      label: labels[key] || key,
      liveRaw,
      liveDisplay: formatTlbCycleLiveDisplay(raw)
    };
  });
}

/**
 * Dropdown options for HOURS schedule TLB counter (Airframe Hours, Hobbs Meter AC, etc.).
 * @param {object|null|undefined} definition
 * @param {object|null|undefined} liveValues - currentUsageTotals.values
 * @returns {Array<{ key: string, label: string, liveRaw: number|null, liveDisplay: string }>}
 */
export function buildTlbHoursOptions(definition, liveValues) {
  const labels = buildTlbHoursLabelsFromDefinition(definition);
  const values = liveValues && typeof liveValues === 'object' ? liveValues : {};

  return TLB_HOURS_CARRIED_FORWARD_KEYS.map((key) => {
    const raw = values[key];
    const liveRaw =
      raw != null && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : null;
    return {
      key,
      label: labels[key] || key,
      liveRaw,
      liveDisplay: formatTlbCycleLiveDisplay(raw)
    };
  });
}

export function recordUsesCyclesUnit(record) {
  if (!record) return false;
  const units = [
    record.limit?.unit,
    record.elapsedTime?.unit,
    record.remainingTime?.unit,
    record.installedValue?.unit
  ].map((u) => String(u || '').trim());
  return units.includes('Cycles');
}
